import {
  formatPrice,
  isBagPass,
  isLegacyReferralReward,
  isMatic,
  isMaticCare,
  isStandardBagPass,
} from "@/utils/ShopifyHelpers";
import { Gift, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { DiscountedPrice } from "@/components/common";
import { cn } from "@/lib/utils";
import type { CartLineNode } from "@/cart/displayCart";
import { getCartBundle } from "@/cart/cartBundle";
import {
  getCartLineDisplayTitle,
  getLineEffectiveTotalAmount,
} from "@/cart/displayCart";
import QuantitySelector from "@/components/QuantitySelector";
import Link from "next/link";
import { isBuilderKitProductId } from "@/utils/builderKit";
import { PET_BRUSH_ROLL_VARIANT_ID } from "@/consts/shopify";
import MaticCareUpsell from "./MaticCareUpsell";
import PetBrushRollUpsell from "./PetBrushRollUpsell";

type SideCartItemProps = {
  item: CartLineNode;
  groupedItems?: CartLineNode[] | undefined;
  isLast: boolean;
  quantity: number;
  maxQuantity: number | null | undefined;
  onChange: (newQuantity: number) => Promise<void> | void;
  onRemove: () => Promise<void> | void;
  onAddMaticCare?: (() => Promise<void> | void) | undefined;
  onAddPetBrushRoll?: (() => Promise<void> | void) | undefined;
  hasSingleMatic: boolean;
  isGift: boolean;
  setIsGift: (value: boolean) => void;
  isMutating?: boolean | undefined;
  onInteractionStart?: ((target: HTMLButtonElement) => void) | undefined;
};

export default function SideCartItem({
  item,
  groupedItems = [item],
  isLast,
  quantity,
  maxQuantity,
  onChange,
  onRemove,
  onAddMaticCare,
  onAddPetBrushRoll,
  hasSingleMatic,
  isGift,
  setIsGift,
  isMutating = false,
  onInteractionStart,
}: SideCartItemProps) {
  const isLockedPromoItem = isLegacyReferralReward(item);
  const isBagPassItem = isBagPass(item);
  const isMaticItem = isMatic(item);
  const isPetBrushRollItem = item.merchandise.id === PET_BRUSH_ROLL_VARIANT_ID;
  const isStandardBagPassItem = isStandardBagPass(item);
  const isDisplayOnlyItem = isLockedPromoItem || isBagPassItem;
  const isBuilderKitItem = isBuilderKitProductId(item.merchandise.product.id);
  const bundle = getCartBundle(item);
  const metafieldCartDescription =
    item.merchandise.product.metafields
      ?.find((m) => m?.key === "cart_description")
      ?.value?.trim() || undefined;
  const productDescription = item.merchandise.product.description?.trim();
  const cartDescription =
    bundle?.note ?? metafieldCartDescription ?? productDescription;
  const displayTitle = bundle?.name ?? getCartLineDisplayTitle(item);
  const productTitle = bundle?.name ?? item.merchandise.product.title;
  const originalTotalAmount = groupedItems.reduce(
    (total, line) =>
      total + Number(line.merchandise.price.amount) * line.quantity,
    0,
  );
  const effectiveTotalAmount = groupedItems.reduce(
    (total, line) => total + getLineEffectiveTotalAmount(line),
    0,
  );
  const hasDiscountedTotal = effectiveTotalAmount < originalTotalAmount;
  const shouldShowFreePrice =
    isLockedPromoItem ||
    (isBagPassItem && effectiveTotalAmount === 0) ||
    (isBuilderKitItem && effectiveTotalAmount === 0);
  const shouldShowOriginalPrice =
    (shouldShowFreePrice && originalTotalAmount > 0) || hasDiscountedTotal;
  const displayPrice = formatPrice(
    effectiveTotalAmount,
    item.merchandise.price.currencyCode,
  );
  const displayOriginalPrice = formatPrice(
    originalTotalAmount,
    item.merchandise.price.currencyCode,
  );
  const productHref =
    bundle !== null
      ? bundle.href
      : isMaticItem || isMaticCare(item) || isPetBrushRollItem
        ? "/product"
        : `/accessories/${item.merchandise.product.handle}`;
  const currentDisplayPrice = shouldShowFreePrice ? "FREE" : displayPrice;
  const originalDisplayPrice = shouldShowOriginalPrice
    ? displayOriginalPrice
    : null;
  const includesMaticCare = bundle?.includesMaticCare === true;
  const shouldShowMaticCareUpsell =
    isMaticItem && !includesMaticCare && Boolean(onAddMaticCare);
  const removeButton = (
    <Button
      variant="ghost"
      size="icon-sm"
      onClick={async (event) => {
        onInteractionStart?.(event.currentTarget);
        await onRemove();
      }}
      className="shrink-0 text-[#1f2022]"
      disabled={isMutating}
      aria-label={`Remove ${productTitle} from cart`}
    >
      <Trash2 className="h-4 w-4" strokeWidth={1} />
    </Button>
  );

  if (isBagPassItem) {
    const bagPassDisplayPrice = shouldShowFreePrice ? "FREE" : displayPrice;

    return (
      <div
        className={cn(!isLast && "mb-6 border-b border-legacy-neutral pb-6")}
      >
        <div className="flex flex-col">
          <div className="flex items-start gap-0">
            <div className="size-[96px] shrink-0 rounded-[8px]">
              {item.merchandise.image && (
                <img
                  src={item.merchandise.image.url}
                  alt={item.merchandise.product.title}
                  className="h-full w-full rounded-[8px] object-cover"
                />
              )}
            </div>
            <div className="flex min-h-[96px] flex-1 flex-col justify-between pl-4">
              <div className="flex flex-col">
                <div className="flex items-center justify-between">
                  <p className="font-ppMori text-[16px] leading-[1.4] tracking-[-0.32px] text-black">
                    {displayTitle}
                  </p>
                  <div className="ml-4 flex shrink-0 items-center gap-2 font-trimMono text-[14px] leading-[1.6] tracking-[0.42px]">
                    {shouldShowOriginalPrice ? (
                      <DiscountedPrice
                        currentPrice={bagPassDisplayPrice}
                        originalPrice={displayOriginalPrice}
                        className="flex items-center gap-2"
                        currentPriceClassName="order-last text-[#1f2022]"
                        originalPriceClassName="order-first text-[#54524e] line-through"
                      />
                    ) : (
                      <span className="text-[#1f2022]">
                        {bagPassDisplayPrice}
                      </span>
                    )}
                  </div>
                </div>
                {cartDescription && (
                  <p className="font-ppMori text-[14px] leading-[1.6] tracking-[-0.28px] text-[#54524e]">
                    {cartDescription}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-4 pt-2">
                {/* Referral reward passes are tied to the Matic count, so the
                    quantity is shown but the controls are disabled. */}
                <QuantitySelector
                  quantity={quantity}
                  onChange={onChange}
                  onRemove={onRemove}
                  maxQuantity={maxQuantity ?? 0}
                  disabled={isMutating || !isStandardBagPassItem}
                  announceChanges={false}
                  itemName={productTitle}
                  onInteractionStart={onInteractionStart}
                />
                {removeButton}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const itemContent = (
    <>
      <div className="size-[96px] shrink-0">
        {item.merchandise.image && (
          <img
            src={item.merchandise.image.url}
            alt={productTitle}
            className="h-full w-full object-cover"
          />
        )}
      </div>
      <div className="flex flex-1 flex-col pl-4">
        <div className="flex items-center justify-between">
          <p className="font-ppMori text-[16px] leading-[1.4] tracking-[-0.32px] text-black">
            {isDisplayOnlyItem ? (
              displayTitle
            ) : (
              <Link href={productHref} className="text-black no-underline">
                {displayTitle}
              </Link>
            )}
          </p>
          <div className="ml-4 flex shrink-0 items-center gap-2 font-trimMono text-[14px] leading-[1.6] tracking-[0.42px]">
            {originalDisplayPrice ? (
              <DiscountedPrice
                currentPrice={currentDisplayPrice}
                originalPrice={originalDisplayPrice}
                className="flex items-center gap-2"
                currentPriceClassName="order-last text-[#1f2022]"
                originalPriceClassName="order-first text-[#54524e] line-through"
              />
            ) : (
              <span className="text-[#1f2022]">{currentDisplayPrice}</span>
            )}
          </div>
        </div>
        {cartDescription && (
          <p className="font-ppMori text-[14px] leading-[1.6] tracking-[-0.28px] text-[#54524e]">
            {cartDescription}
          </p>
        )}
        {!isLockedPromoItem && (
          <div className="flex flex-col gap-4 pt-4">
            <div className="flex items-center gap-4">
              {!isBagPassItem && (
                <QuantitySelector
                  quantity={quantity}
                  onChange={onChange}
                  onRemove={onRemove}
                  maxQuantity={maxQuantity ?? 0}
                  disabled={isMutating}
                  announceChanges={false}
                  itemName={productTitle}
                  onInteractionStart={onInteractionStart}
                />
              )}
              {removeButton}
            </div>
            {isMaticItem && hasSingleMatic && (
              <div className="flex cursor-default items-center justify-between">
                <div className="flex items-center gap-2">
                  <Gift className="size-4 shrink-0 stroke-1 text-[#54524e]" />
                  <span className="font-trimMono text-[14px] uppercase leading-[1.6] tracking-[0.42px] text-[#54524e]">
                    <span className="md:hidden">GIFT</span>
                    <span className="hidden md:inline">
                      MAKE THIS MATIC A GIFT
                    </span>
                  </span>
                </div>
                <Switch
                  checked={isGift}
                  onCheckedChange={setIsGift}
                  aria-label="Make this Matic a gift"
                  className="!h-[24px] !w-[42px] shrink-0 [&>span]:!h-5 [&>span]:!w-5 [&>span]:data-[state=checked]:!translate-x-[18px]"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
  const maticCareUpsell =
    shouldShowMaticCareUpsell && onAddMaticCare ? (
      <MaticCareUpsell
        onAddMaticCare={onAddMaticCare}
        onInteractionStart={onInteractionStart}
        isMutating={isMutating}
        quantity={quantity}
      />
    ) : null;
  const petBrushRollUpsell =
    isMaticItem && onAddPetBrushRoll ? (
      <PetBrushRollUpsell
        onAddPetBrushRoll={onAddPetBrushRoll}
        onInteractionStart={onInteractionStart}
        isMutating={isMutating}
        quantity={quantity}
      />
    ) : null;

  return (
    <div className={cn(!isLast && "mb-6 border-b border-legacy-neutral pb-6")}>
      <div className="flex items-start gap-0">{itemContent}</div>
      {maticCareUpsell}
      {petBrushRollUpsell}
    </div>
  );
}
